import { useState, useEffect, useRef, useCallback, useMemo, memo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Plus, Trash2, Printer, Check, GripVertical, Edit, CheckCheck } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useIsMobile } from '@/hooks/use-mobile';
import { useNavigate } from 'react-router-dom';
import { escapeHtml, safeDisplayText, safeSaveText, safePrintText } from '@/lib/utils';
import { secureLog } from '@/lib/security';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { supabase } from '@/integrations/supabase/client';
import { MobileItemEditor } from '@/components/MobileItemEditor';
import { useAuth } from '@/hooks/useAuth';
import { trackEvent } from '@/components/GoogleAnalytics';

const MAX_ITEMS_PER_CHECKLIST = 25;

interface ChecklistItem {
  id: string;
  target: boolean;
  ready: boolean;
  insert: boolean;
  item: string;
  comment: string;
}

interface SortableChecklistRowProps {
  item: ChecklistItem;
  updateItem: (id: string, field: keyof ChecklistItem, value: string | boolean) => void;
  removeItem: (id: string) => void;
  addItem: () => void;
  isLastItem: boolean;
  isMobile: boolean;
  isUserChecklist: boolean;
  onMobileEdit: (itemId: string) => void;
}

const SortableChecklistRow = memo(({ item, updateItem, removeItem, addItem, isLastItem, isMobile, isUserChecklist, onMobileEdit }: SortableChecklistRowProps) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id: item.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <TableRow ref={setNodeRef} style={style} {...attributes} className="group">
      <TableCell className="w-12 p-2">
        <div className="flex items-center justify-center">
          <GripVertical 
            className="h-4 w-4 text-muted-foreground cursor-grab active:cursor-grabbing hover:text-primary group-hover:text-primary" 
            {...listeners}
          />
        </div>
      </TableCell>
      <TableCell className="w-12 p-2">
        <div className="flex items-center justify-center">
          <Checkbox
            checked={item.target}
            onCheckedChange={(checked) => updateItem(item.id, 'target', checked as boolean)}
            className="h-4 w-4"
            aria-label="Mark item for swim phase"
          />
        </div>
      </TableCell>
      <TableCell className="w-12 p-2">
        <div className="flex items-center justify-center">
          <Checkbox
            checked={item.ready}
            onCheckedChange={(checked) => updateItem(item.id, 'ready', checked as boolean)}
            className="h-4 w-4"
            aria-label="Mark item for bike phase"
          />
        </div>
      </TableCell>
      <TableCell className="w-12 p-2">
        <div className="flex items-center justify-center">
          <Checkbox
            checked={item.insert}
            onCheckedChange={(checked) => updateItem(item.id, 'insert', checked as boolean)}
            className="h-4 w-4"
            aria-label="Mark item for run phase"
          />
        </div>
      </TableCell>
      <TableCell className="p-2">
        <Input
          value={item.item}
          onChange={(e) => updateItem(item.id, 'item', e.target.value)}
          onClick={isMobile && isUserChecklist ? () => onMobileEdit(item.id) : undefined}
          readOnly={isMobile && isUserChecklist}
          placeholder="Item description..."
          className="w-full border-none shadow-none p-0 h-auto max-w-[calc(100vw-12rem)] md:max-w-none"
          aria-label="Item description"
          style={{ boxSizing: 'border-box' }}
        />
      </TableCell>
      <TableCell className="p-2">
        <Input
          value={item.comment}
          onChange={(e) => updateItem(item.id, 'comment', e.target.value)}
          onClick={isMobile && isUserChecklist ? () => onMobileEdit(item.id) : undefined}
          readOnly={isMobile && isUserChecklist}
          placeholder="Notes..."
          className="w-full border-none shadow-none p-0 h-auto max-w-[calc(100vw-12rem)] md:max-w-none"
          aria-label="Notes for this item"
          style={{ boxSizing: 'border-box' }}
        />
      </TableCell>
      <TableCell className="w-16 p-2">
        <div className="flex items-center justify-center">
          <Button
            onClick={() => removeItem(item.id)}
            onKeyDown={(e) => {
              if (e.key === 'Tab' && !e.shiftKey && isLastItem) {
                e.preventDefault();
                addItem();
              }
            }}
            variant="ghost"
            size="sm"
            className="text-muted-foreground hover:text-destructive p-1 h-auto"
            aria-label="Delete this checklist item"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </TableCell>
    </TableRow>
  );
});

interface ChecklistModalProps {
  isOpen: boolean;
  onClose: () => void;
  phaseId: string;
  phaseTitle: string;
  sectionName: string;
  onPhaseTitleUpdate: (phaseId: string, newTitle: string) => void;
  preloadedItems?: ChecklistItem[];
  isSampleChecklist?: boolean;
}

export const ChecklistModal = ({ 
  isOpen, 
  onClose, 
  phaseId, 
  phaseTitle, 
  sectionName, 
  onPhaseTitleUpdate,
  preloadedItems = [],
  isSampleChecklist = false
}: ChecklistModalProps) => {
  // Helper function to create default empty items
  const createDefaultItems = (): ChecklistItem[] => {
    return Array.from({ length: 5 }, () => ({
      id: crypto.randomUUID(),
      target: false,
      ready: false,
      insert: false,
      item: '',
      comment: ''
    }));
  };

  // Save items immediately (synchronously for guest, async for authenticated)
  const saveItemsImmediately = async (itemsToSave: ChecklistItem[]) => {
    if (!user) {
      // Guest mode: save to sessionStorage synchronously
      const itemsForStorage = itemsToSave.map((item, index) => ({
        id: item.id || crypto.randomUUID(),
        target: item.target || false,
        ready: item.ready || false,
        insert: item.insert || false,
        item: item.item || '',
        comment: item.comment || '',
        display_order: index
      }));
      sessionStorage.setItem(`checklist-items-${phaseId}`, JSON.stringify(itemsForStorage));
    } else {
      // Authenticated: save to Supabase
      await saveItemsToSupabase(itemsToSave);
    }
  };

  const [items, setItems] = useState<ChecklistItem[]>(() => {
    secureLog.debug('ChecklistModal: Initial state - preloadedItems length:', preloadedItems.length);
    return preloadedItems.length > 0 ? preloadedItems : createDefaultItems();
  });
  const [initialItems, setInitialItems] = useState<ChecklistItem[]>(() => {
    return preloadedItems.length > 0 ? preloadedItems : createDefaultItems();
  });
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const saveInProgressRef = useRef(false);
  const autoSaveTimerRef = useRef<NodeJS.Timeout | null>(null);
  const [editedTitle, setEditedTitle] = useState(phaseTitle);
  const { toast } = useToast();
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  // Mobile editor state
  const [mobileEditorOpen, setMobileEditorOpen] = useState(false);
  const [mobileEditingItem, setMobileEditingItem] = useState<ChecklistItem | null>(null);
  
  // Mobile title editor state
  const [mobileTitleEditorOpen, setMobileTitleEditorOpen] = useState(false);
  const [tempEditedTitle, setTempEditedTitle] = useState(phaseTitle);
  
  // Delete confirmation state
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<string | null>(null);
  
  // Clear all checkboxes confirmation state
  const [clearCheckboxesConfirmOpen, setClearCheckboxesConfirmOpen] = useState(false);
  
  // Determine if this is a user checklist (has preloaded items OR is a guest user)
  const isUserChecklist = preloadedItems.length > 0 || !user;

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const updateItem = useCallback((id: string, field: keyof ChecklistItem, value: string | boolean) => {
    setItems(items => items.map(item => 
      item.id === id ? { 
        ...item, 
        [field]: value
      } : item
    ));
  }, []);

  const handleDragEnd = useCallback((event: any) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      setItems((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over.id);

        return arrayMove(items, oldIndex, newIndex);
      });
    }
  }, []);

  // Mobile editor handlers
  const handleMobileEdit = useCallback((itemId: string) => {
    const item = items.find(i => i.id === itemId);
    if (item) {
      setMobileEditingItem(item);
      setMobileEditorOpen(true);
    }
  }, [items]);

  const handleMobileSave = useCallback((itemText: string, notes: string) => {
    if (mobileEditingItem) {
      setItems(items => items.map(item => 
        item.id === mobileEditingItem.id 
          ? { ...item, item: itemText, comment: notes }
          : item
      ));
      setMobileEditingItem(null);
    }
  }, [mobileEditingItem]);

  // Update items when preloadedItems change OR when modal opens for guest users
  useEffect(() => {
    secureLog.debug('ChecklistModal: preloadedItems changed', { count: preloadedItems.length });
    
    if (isOpen && !user && phaseId) {
      // Load from sessionStorage for guest users
      const savedItems = sessionStorage.getItem(`checklist-items-${phaseId}`);
      if (savedItems) {
        const parsedItems = JSON.parse(savedItems);
        setItems(parsedItems);
        setInitialItems(parsedItems);
        return;
      }
    }
    
    if (preloadedItems.length > 0) {
      secureLog.debug('ChecklistModal: Setting items from preloadedItems');
      setItems(preloadedItems);
      setInitialItems(preloadedItems);
    } else {
      secureLog.debug('ChecklistModal: Setting default items');
      const defaultItems = createDefaultItems();
      setItems(defaultItems);
      setInitialItems(defaultItems);
    }
  }, [preloadedItems, isOpen, phaseId, user]);

  // Save items to Supabase - returns the saved items with database IDs
  const saveItemsToSupabase = async (itemsToSave: ChecklistItem[]): Promise<ChecklistItem[]> => {
    secureLog.debug('saveItemsToSupabase called', { itemCount: itemsToSave.length, phaseId });
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      secureLog.debug('Current user for saving checklist items', { userId: user?.id });
      
      if (!user) {
        secureLog.warn('No user found, cannot save items');
        return;
      }

      secureLog.debug('Saving items', { 
        itemCount: itemsToSave.length
      });

      // First, get the section_id for this phase
      const { data: phaseData, error: phaseError } = await supabase
        .from('checklist_phases')
        .select('section_id')
        .eq('id', phaseId)
        .maybeSingle();

      if (phaseError || !phaseData) {
        secureLog.error('Failed to find section for phase', { phaseId, phaseError });
        throw new Error('Failed to find section for this phase');
      }

      // Fetch existing items from database
      const { data: existingItems, error: fetchError } = await supabase
        .from('checklist_items')
        .select('*')
        .eq('phase_id', phaseId)
        .eq('user_id', user.id);

      if (fetchError) {
        secureLog.error('Error fetching existing items', fetchError);
        throw fetchError;
      }

      secureLog.debug('Fetched existing items', { existingCount: existingItems?.length || 0 });

      // Create a map of existing items by ID for easy lookup
      const existingItemsMap = new Map((existingItems || []).map(item => [item.id, item]));
      
      // Categorize items
      const itemsToUpdate: any[] = [];
      const itemsToInsert: any[] = [];
      const existingIds = new Set((existingItems || []).map(item => item.id));
      const currentIds = new Set(itemsToSave.filter(item => item.id).map(item => item.id));

      itemsToSave.forEach((item, index) => {
        const itemData = {
          phase_id: phaseId,
          section_id: phaseData.section_id,
          user_id: user.id,
          display_order: index,
          is_secured: item.target,
          is_ready: item.ready,
          is_bundled: item.insert,
          item_text: safeSaveText(item.item, 500),
          notes: safeSaveText(item.comment, 1000)
        };

        if (item.id && existingIds.has(item.id)) {
          // Existing item - update it
          itemsToUpdate.push({ id: item.id, ...itemData });
        } else {
          // New item - insert it
          itemsToInsert.push(itemData);
        }
      });

      // Items to delete are those in the database but not in current list
      const itemsToDelete = (existingItems || [])
        .filter(item => !currentIds.has(item.id))
        .map(item => item.id);

      secureLog.debug('Categorized changes', {
        toUpdate: itemsToUpdate.length,
        toInsert: itemsToInsert.length,
        toDelete: itemsToDelete.length
      });

      // Execute updates - Use bulk upsert instead of sequential loop
      if (itemsToUpdate.length > 0) {
        const { error: updateError } = await supabase
          .from('checklist_items')
          .upsert(
            itemsToUpdate,
            { 
              onConflict: 'id',
              ignoreDuplicates: false 
            }
          );

        if (updateError) {
          secureLog.error('Error updating items', updateError);
          throw updateError;
        }
        
        secureLog.debug('Successfully updated items', { count: itemsToUpdate.length });
      }

      // Execute inserts
      if (itemsToInsert.length > 0) {
        const { data: insertedData, error: insertError } = await supabase
          .from('checklist_items')
          .insert(itemsToInsert)
          .select();

        if (insertError) {
          secureLog.error('Error inserting items', insertError);
          throw insertError;
        }

        secureLog.debug('Successfully inserted items', { count: itemsToInsert.length });

        // Update local state with new IDs from database
        if (insertedData && insertedData.length > 0) {
          // Create a map of temp ID -> real ID for quick lookup
          const idMap = new Map<string, string>();
          let insertedIndex = 0;
          
          for (let i = 0; i < itemsToSave.length && insertedIndex < insertedData.length; i++) {
            if (!itemsToSave[i].id || !existingIds.has(itemsToSave[i].id)) {
              idMap.set(itemsToSave[i].id, insertedData[insertedIndex].id);
              insertedIndex++;
            }
          }
          
          // Update items state functionally - only update IDs, preserve everything else
          setItems(prevItems => prevItems.map(item => 
            idMap.has(item.id) ? { ...item, id: idMap.get(item.id)! } : item
          ));
          
          // Update initialItems to reflect what's actually in the database
          const finalItems = itemsToSave.map(item => {
            if (idMap.has(item.id)) {
              return { ...item, id: idMap.get(item.id)! };
            }
            return item;
          });
          setInitialItems(finalItems);
        }
      }

      // Execute deletes
      if (itemsToDelete.length > 0) {
        const { error: deleteError } = await supabase
          .from('checklist_items')
          .delete()
          .in('id', itemsToDelete)
          .eq('user_id', user.id);

        if (deleteError) {
          secureLog.error('Error deleting items', deleteError);
          throw deleteError;
        }

        secureLog.debug('Successfully deleted items', { count: itemsToDelete.length });
      }

      secureLog.info('Successfully saved all checklist items');
      
      // If no inserts happened, still sync initialItems
      setInitialItems(itemsToSave);
      return itemsToSave;
    } catch (error) {
      secureLog.error('Error saving items to Supabase', error);
      toast({
        title: "Error",
        description: "Failed to save checklist items",
        variant: "destructive",
      });
      return itemsToSave; // Return original items on error
    }
  };



  // Auto-save items when they change
  useEffect(() => {
    if (items.length > 0) {
      // Clear any existing timer
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }
      
      autoSaveTimerRef.current = setTimeout(async () => {
        if (saveInProgressRef.current) {
          secureLog.debug('Save already in progress, skipping auto-save');
          return;
        }
        
        saveInProgressRef.current = true;
        
        try {
          if (!user) {
            // Guest mode: save to sessionStorage
            const itemsToSave = items.map((item, index) => ({
              id: item.id || crypto.randomUUID(),
              target: item.target || false,
              ready: item.ready || false,
              insert: item.insert || false,
              item: item.item || '',
              comment: item.comment || '',
              display_order: index
            }));
            
            sessionStorage.setItem(`checklist-items-${phaseId}`, JSON.stringify(itemsToSave));
            setInitialItems(items);
          } else {
            // Authenticated: save to Supabase - this now updates initialItems internally
            await saveItemsToSupabase(items);
          }
        } finally {
          saveInProgressRef.current = false;
        }
      }, 1000);

      return () => {
        if (autoSaveTimerRef.current) {
          clearTimeout(autoSaveTimerRef.current);
        }
      };
    }
  }, [items, phaseId, user]);

  const addItem = useCallback(() => {
    setItems(items => {
      if (items.length >= MAX_ITEMS_PER_CHECKLIST) {
        toast({
          title: "Maximum items reached",
          description: "You can only have up to 25 items per checklist.",
          variant: "destructive",
        });
        return items;
      }

      const newItem: ChecklistItem = {
        id: crypto.randomUUID(),
        target: false,
        ready: false,
        insert: false,
        item: '',
        comment: ''
      };
      const updatedItems = [...items, newItem];
      
      // Track item addition
      trackEvent('checklist_item_added', {
        phase_id: phaseId,
        total_items: updatedItems.length,
        is_sample: isSampleChecklist,
        user_authenticated: !!user
      });
      
      return updatedItems;
    });
  }, [user, navigate, toast, phaseId, isSampleChecklist]);

  const removeItem = useCallback((id: string) => {
    setItemToDelete(id);
    setDeleteConfirmOpen(true);
  }, []);

  const confirmDelete = () => {
    if (itemToDelete) {
      const updatedItems = items.filter(item => item.id !== itemToDelete);
      setItems(updatedItems);
      setItemToDelete(null);
    }
    setDeleteConfirmOpen(false);
  };

  const handleTitleSave = () => {
    // Call parent update function for both guest and authenticated users
    onPhaseTitleUpdate(phaseId, editedTitle);
    setIsEditingTitle(false);
  };
  
  const handleClearAllCheckboxes = async () => {
    // Clear all checkboxes (target, ready, insert) for all items
    const clearedItems = items.map(item => ({
      ...item,
      target: false,
      ready: false,
      insert: false
    }));
    
    setItems(clearedItems);
    
    // Save immediately
    await saveItemsImmediately(clearedItems);
    
    toast({
      title: "All checkboxes cleared",
      description: "All S, B, and R checkboxes have been cleared.",
    });
    
    setClearCheckboxesConfirmOpen(false);
  };

  const handleClose = async (open: boolean) => {
    // Only save and close when dialog is being closed (open = false)
    if (!open) {
      // Cancel any pending auto-save
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
        autoSaveTimerRef.current = null;
      }
      
      // Wait for any in-progress save to complete (with timeout to prevent hanging)
      let saveWaitCount = 0;
      const maxWaitIterations = 200; // 2 second max wait (200 * 10ms)
      
      while (saveInProgressRef.current && saveWaitCount < maxWaitIterations) {
        await new Promise(resolve => setTimeout(resolve, 10));
        saveWaitCount++;
      }
      
      // Use reference comparison first (instant check)
      // This catches most cases where no changes were made
      const hasReferenceChange = items !== initialItems;
      
      // Only do a final save if:
      // 1. There's a reference change (user made edits)
      // 2. No save is currently in progress (safety check)
      if (hasReferenceChange && !saveInProgressRef.current) {
        saveInProgressRef.current = true;
        try {
          await saveItemsImmediately(items);
        } catch (error) {
          // Errors are logged in development only via secureLog
          // Still close the modal even if save fails
          // The auto-save system would have already saved most changes
        } finally {
          saveInProgressRef.current = false;
        }
      }
      
      // Track modal close with engagement status
      const hasChanges = JSON.stringify(items) !== JSON.stringify(initialItems);
      trackEvent('checklist_modal_closed', {
        had_changes: hasChanges,
        item_count: items.length,
        is_sample: isSampleChecklist,
        user_authenticated: !!user
      });
      
      onClose();
    }
  };


  const printChecklist = () => {
    // Best-effort save (don't block printing)
    void saveItemsImmediately(items).catch(() => {});

    const originalTitle = document.title;
    document.title = `${sectionName}: ${editedTitle}`;

    // Open a new print window (same approach as section print which works reliably)
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      document.title = originalTitle;
      toast({
        title: "Popup blocked",
        description: "Please allow popups to print your checklist.",
        variant: "destructive"
      });
      return;
    }

    // Build HTML with inline CSS (copied from working section print)
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <title>${document.title}</title>
        <style>
          body { 
            font-family: Arial, sans-serif; 
            padding: 20px;
            margin: 0;
          }
          h1 { 
            color: #000000; 
            margin-bottom: 30px; 
            text-align: center; 
            font-weight: 300; 
          }
          table { 
            width: 100%; 
            border-collapse: collapse; 
            margin-bottom: 30px; 
          }
          th, td { 
            border: 1px solid #ccc; 
            padding: 8px; 
            text-align: left; 
            font-weight: 400;
            white-space: nowrap;
          }
          th { 
            background-color: #f5f5f5; 
            font-weight: 400; 
          }
          .checkbox { 
            width: 20px; 
            height: 20px; 
            border: 1px solid #ccc; 
            appearance: none; 
            -webkit-appearance: none; 
            -moz-appearance: none; 
            cursor: pointer; 
          }
          .checkbox:checked { 
            background-color: #ffffff; 
            position: relative; 
          }
          .checkbox:checked::after { 
            content: '✓'; 
            position: absolute; 
            color: #000000; 
            font-size: 14px; 
            font-weight: bold; 
            top: 50%; 
            left: 50%; 
            transform: translate(-50%, -50%); 
          }
          .checkbox-col { 
            width: 40px; 
            text-align: center; 
          }
          @media print { 
            @page { 
              margin: 0.5cm; 
            }
          }
        </style>
      </head>
      <body>
        <h1>${safePrintText(`${sectionName}: ${editedTitle}`)}</h1>
        <table>
          <thead>
            <tr>
              <th class="checkbox-col">S</th>
              <th class="checkbox-col">B</th>
              <th class="checkbox-col">R</th>
              <th>Item</th>
              <th>Notes</th>
            </tr>
          </thead>
          <tbody>
            ${items.map(item => `
              <tr>
                <td class="checkbox-col"><input type="checkbox" class="checkbox" ${item.target ? 'checked' : ''} /></td>
                <td class="checkbox-col"><input type="checkbox" class="checkbox" ${item.ready ? 'checked' : ''} /></td>
                <td class="checkbox-col"><input type="checkbox" class="checkbox" ${item.insert ? 'checked' : ''} /></td>
                <td>${safePrintText(item.item)}</td>
                <td>${safePrintText(item.comment || '')}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
      </body>
      </html>
    `);

    printWindow.document.close();
    printWindow.focus();
      // Close window after print dialog is dismissed
      printWindow.onafterprint = () => {
        printWindow.close();
        document.title = originalTitle;
      };

      setTimeout(() => {
        printWindow.print();
      }, 250);

      // Fallback: restore title if onafterprint doesn't fire
      setTimeout(() => {
        document.title = originalTitle;
      }, 5000);
  };

  return (
    <TooltipProvider>
      <Dialog open={isOpen} onOpenChange={handleClose}>
        <DialogContent
          className="max-w-6xl max-h-[90vh] md:max-h-[90vh] md:min-h-[600px] flex flex-col"
          aria-describedby="checklist-modal-description"
        >
        <DialogHeader>
          <div id="checklist-modal-description" className="sr-only">
            Interactive checklist for {sectionName} - {phaseTitle}. Add, edit, and organize your race preparation items. Use checkboxes to mark items for swim (S), bike (B), or run (R) phases.
          </div>
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 md:gap-4">
            <div className="flex flex-col gap-1 md:gap-2 w-full">
              {/* Desktop layout */}
              <div className="hidden md:flex flex-row items-baseline gap-2 min-h-[2.5rem]">
                <span className="text-xl font-normal text-foreground flex-shrink-0">{sectionName}</span>
                <span className="text-muted-foreground text-xl flex-shrink-0">-</span>
                <div className="flex items-center gap-2 min-w-0 flex-shrink">
                  {isEditingTitle ? (
                    <Input
                      value={editedTitle}
                      onChange={(e) => setEditedTitle(e.target.value.slice(0, 30))}
                      onBlur={handleTitleSave}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleTitleSave();
                        if (e.key === 'Escape') {
                          setEditedTitle(phaseTitle);
                          setIsEditingTitle(false);
                        }
                      }}
                      className="text-xl font-normal !text-foreground h-10 px-2 py-1 border-0 bg-transparent shadow-none focus:ring-0 focus:ring-offset-0 min-w-0 flex-shrink max-w-[calc(100vw-12rem)]"
                      maxLength={30}
                      autoFocus
                      spellCheck={false}
                    />
                  ) : (
                    <span 
                      className="text-xl font-normal text-foreground cursor-pointer hover:bg-muted/50 px-2 py-1 rounded h-10 flex items-center min-w-0 flex-shrink"
                      onClick={() => setIsEditingTitle(true)}
                    >
                      {editedTitle}
                    </span>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setIsEditingTitle(true)}
                    className="h-8 w-8 p-0 hover:bg-muted flex-shrink-0"
                    aria-label={`Edit checklist name: ${editedTitle}`}
                  >
                    <Edit className="h-4 w-4" aria-hidden="true" />
                  </Button>
                </div>
              </div>
              
              {/* Mobile layout - Single row */}
              <div className="md:hidden flex items-center justify-between gap-2 mt-8">
                {/* Left: Title with ellipsis */}
                <div className="flex items-center gap-1 min-w-0 flex-1">
                  <span className="text-sm font-normal text-foreground truncate">
                    {sectionName} - {editedTitle}
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setTempEditedTitle(editedTitle);
                      setMobileTitleEditorOpen(true);
                    }}
                    className="h-6 w-6 p-0 hover:bg-muted flex-shrink-0"
                    aria-label={`Edit checklist name: ${editedTitle}`}
                  >
                    <Edit className="h-3 w-3" aria-hidden="true" />
                  </Button>
                </div>
                
                {/* Right: Action icons */}
                <div className="flex items-center gap-1 flex-shrink-0">
                  <AlertDialog open={clearCheckboxesConfirmOpen} onOpenChange={setClearCheckboxesConfirmOpen}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <AlertDialogTrigger asChild>
                          <Button 
                            variant="ghost" 
                            size="icon"
                            className="h-8 w-8 p-0 flex-shrink-0"
                            aria-label="Clear all checkboxes"
                          >
                            <CheckCheck className="h-4 w-4" />
                          </Button>
                        </AlertDialogTrigger>
                      </TooltipTrigger>
                      <TooltipContent>Clear all checkboxes</TooltipContent>
                    </Tooltip>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Clear All Checkboxes</AlertDialogTitle>
                        <AlertDialogDescription>
                          Are you sure you want to clear all checkboxes in this checklist? This will uncheck all S, B, and R boxes but keep your items and notes.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={handleClearAllCheckboxes}>
                          Clear All
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                  
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button 
                        onClick={printChecklist} 
                        variant="ghost" 
                        size="icon"
                        className="h-8 w-8 p-0 flex-shrink-0"
                        aria-label="Print checklist"
                      >
                        <Printer className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Print checklist</TooltipContent>
                  </Tooltip>
                </div>
              </div>
            </div>
            
            {/* Desktop action buttons */}
            <div className="hidden md:flex items-center gap-2">
              <AlertDialog open={clearCheckboxesConfirmOpen} onOpenChange={setClearCheckboxesConfirmOpen}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <AlertDialogTrigger asChild>
                      <Button 
                        variant="ghost" 
                        size="icon"
                        className="h-10 w-10"
                        aria-label="Clear all checkboxes"
                      >
                        <CheckCheck className="h-4 w-4" />
                      </Button>
                    </AlertDialogTrigger>
                  </TooltipTrigger>
                  <TooltipContent>Clear all checkboxes</TooltipContent>
                </Tooltip>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Clear All Checkboxes</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to clear all checkboxes in this checklist?
              </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={handleClearAllCheckboxes}>
                      Clear All
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
              
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button 
                    onClick={printChecklist} 
                    variant="ghost" 
                    size="icon"
                    className="h-10 w-10"
                    aria-label="Print checklist"
                  >
                    <Printer className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Print checklist</TooltipContent>
              </Tooltip>
            </div>
          </div>
        </DialogHeader>
        
        <div className="flex-1 overflow-auto">
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <div className="overflow-x-auto">
              <Table role="table" aria-label={`Checklist items for ${phaseTitle} in ${sectionName}`}>
                <caption className="sr-only">
                  Checklist items table with columns for reordering, swim/bike/run phases, item descriptions, notes, and actions. Use drag handles to reorder items.
                </caption>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12 text-center" scope="col" aria-label="Drag to reorder items">Drag</TableHead>
                    <TableHead className="w-12 text-center" scope="col" aria-label="Swim phase">S</TableHead>
                    <TableHead className="w-12 text-center" scope="col" aria-label="Bike phase">B</TableHead>
                    <TableHead className="w-12 text-center" scope="col" aria-label="Run phase">R</TableHead>
                    <TableHead scope="col">Item</TableHead>
                    <TableHead scope="col">Notes</TableHead>
                    <TableHead className="w-16 text-center" scope="col">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  <SortableContext
                    items={items.map(item => item.id)}
                    strategy={verticalListSortingStrategy}
                  >
                     {items.map((item, index) => (
                       <SortableChecklistRow
                         key={item.id}
                         item={item}
                         updateItem={updateItem}
                         removeItem={removeItem}
                         addItem={addItem}
                         isLastItem={index === items.length - 1}
                         isMobile={isMobile}
                         isUserChecklist={isUserChecklist}
                         onMobileEdit={handleMobileEdit}
                       />
                     ))}
                  </SortableContext>
                </TableBody>
              </Table>
            </div>
          </DndContext>
          
          <div className="mt-4">
            <div
              onClick={items.length >= MAX_ITEMS_PER_CHECKLIST ? undefined : addItem}
              className={`group border-2 border-dashed border-accent rounded-sm p-4 cursor-pointer flex flex-col items-center justify-center text-center bg-card transition-all ${
                items.length >= MAX_ITEMS_PER_CHECKLIST 
                  ? 'opacity-50 cursor-not-allowed' 
                  : 'hover:shadow-sm hover:bg-muted'
              }`}
            >
              <Plus className={`w-6 h-6 text-accent mb-2 ${items.length < MAX_ITEMS_PER_CHECKLIST ? 'transition-transform group-hover:scale-110' : ''}`} />
              <div className={`text-muted-foreground font-normal text-base ${items.length < MAX_ITEMS_PER_CHECKLIST ? 'transition-colors duration-300 group-hover:text-accent' : ''}`}>
                Add Item ({items.length}/{MAX_ITEMS_PER_CHECKLIST})
              </div>
            </div>
          </div>
          
          {/* Add explanation text for S, B, R */}
          {items.length > 0 && (
            <div className="bg-muted/20 p-3 rounded-lg text-sm mt-4">
              <div className="grid grid-cols-3 gap-4 text-muted-foreground text-xs">
                <div><span className="font-normal">Secured (S):</span> Check this box when you possess the item.</div>
                <div><span className="font-normal">Bundled (B):</span> Check this box when you have staged the item for packing.</div>
                <div><span className="font-normal">Ready (R):</span> Check this box when you've physically packed the item.</div>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
      
      {/* Mobile Item Editor */}
      <MobileItemEditor
        key={mobileEditingItem?.id || 'no-item'}
        isOpen={mobileEditorOpen}
        onClose={() => setMobileEditorOpen(false)}
        itemText={mobileEditingItem?.item || ''}
        notes={mobileEditingItem?.comment || ''}
        onSave={handleMobileSave}
      />
      
      {/* Mobile Title Editor Dialog */}
      <Dialog open={mobileTitleEditorOpen} onOpenChange={setMobileTitleEditorOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-center">Edit Checklist Name</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Input
                value={tempEditedTitle}
                onChange={(e) => setTempEditedTitle(e.target.value.slice(0, 30))}
                maxLength={30}
                className="text-base"
                placeholder="Enter checklist name"
                autoFocus
              />
              <p className="text-xs text-muted-foreground mt-1">
                {tempEditedTitle.length}/30 characters
              </p>
            </div>
            <div className="space-y-2">
              <Button
                onClick={() => {
                  setEditedTitle(tempEditedTitle);
                  onPhaseTitleUpdate(phaseId, tempEditedTitle);
                  setMobileTitleEditorOpen(false);
                }}
                variant="accent"
                className="w-full"
              >
                Save Changes
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setTempEditedTitle(editedTitle);
                  setMobileTitleEditorOpen(false);
                }}
                className="w-full"
              >
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Item</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove this item? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setDeleteConfirmOpen(false)}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-destructive hover:bg-destructive/90 text-white">
              Remove Item
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </TooltipProvider>
  );
};